import {
  createContext,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../../lib/cn";
import { OverlayPortal, useOverlayLayer } from "./overlay";
import { RouterLink } from "./router-link";

interface DropdownContextValue {
  open: boolean;
  setOpen(open: boolean): void;
  /** Closes the menu and hands focus back to the trigger. */
  closeToTrigger(): void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  focusFirstItem(): void;
  focusLastItem(): void;
}

interface DropdownMenuProps {
  children: ReactNode;
  className?: string;
}

interface DropdownMenuTriggerProps {
  "aria-label"?: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  /**
   * `field` wears the form-control skin, matching the selects it sits beside.
   * `bare` keeps only the trigger's behaviour and focus treatment and leaves
   * size, shape, and colour to the caller — `cn` concatenates rather than
   * resolving Tailwind conflicts, so a caller-supplied pill cannot reliably
   * override the field skin from the outside.
   */
  variant?: "field" | "bare";
}

interface DropdownMenuContentProps {
  children: ReactNode;
  align?: "start" | "end";
  className?: string;
}

interface DropdownMenuItemProps {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  href?: string;
  onSelect?(): void;
  tone?: "default" | "danger";
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

export function DropdownMenu({ children, className }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Every dismissal that is not a deliberate move elsewhere goes through here.
  // What closes the menu is the focused element — an item, or the panel itself
  // — so unmounting it without this drops focus to <body> and a keyboard reader
  // resumes from the top of the document. Tab is the exception: it is already a
  // move to the next control, so it only closes.
  const closeToTrigger = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  }, []);

  useOverlayLayer(open, { onEscape: closeToTrigger, onTab: () => setOpen(false) });

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      // The content is portaled to document.body, so "inside the menu" means
      // inside the trigger's tree OR inside the portaled panel.
      if (rootRef.current?.contains(target)) return;
      if (contentRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open]);

  const focusFirstItem = useCallback(() => {
    queueMicrotask(() => dropdownItems(contentRef.current)[0]?.focus({ preventScroll: true }));
  }, []);
  const focusLastItem = useCallback(() => {
    queueMicrotask(() => dropdownItems(contentRef.current).at(-1)?.focus({ preventScroll: true }));
  }, []);

  const contextValue = useMemo(
    () => ({
      open,
      setOpen,
      closeToTrigger,
      triggerRef,
      contentRef,
      focusFirstItem,
      focusLastItem,
    }),
    [open, closeToTrigger, focusFirstItem, focusLastItem],
  );

  return (
    <DropdownContext.Provider value={contextValue}>
      <div className={cn("relative", className)} ref={rootRef}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

const TRIGGER_BASE =
  "inline-flex items-center text-left transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-page-bg)] disabled:cursor-not-allowed disabled:opacity-50";

const TRIGGER_FIELD =
  "h-[var(--field-h)] gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm text-[var(--color-text)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-2)]";

export function DropdownMenuTrigger({
  "aria-label": ariaLabel,
  children,
  className,
  disabled,
  variant = "field",
}: DropdownMenuTriggerProps) {
  const menu = useDropdownMenu();

  return (
    <button
      aria-label={ariaLabel}
      aria-expanded={menu.open}
      aria-haspopup="menu"
      className={cn(TRIGGER_BASE, variant === "field" && TRIGGER_FIELD, className)}
      disabled={disabled}
      onClick={() => menu.setOpen(!menu.open)}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          menu.setOpen(true);
          menu.focusFirstItem();
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          menu.setOpen(true);
          menu.focusLastItem();
        }
      }}
      ref={menu.triggerRef}
      type="button"
    >
      {children}
    </button>
  );
}

/** How long a pause resets the typeahead buffer (the WAI-ARIA menu default). */
const TYPEAHEAD_RESET_MS = 500;
const CONTENT_GAP_PX = 6;

export function DropdownMenuContent({
  children,
  align = "end",
  className,
}: DropdownMenuContentProps) {
  const menu = useDropdownMenu();
  // Fixed coordinates measured from the trigger. Portaled to the body because
  // in-tree absolute positioning gets clipped by any scrolling/overflow-hidden
  // ancestor (Panel bodies do exactly that).
  const [position, setPosition] = useState<{ top: number; left?: number; right?: number } | null>(
    null,
  );
  const [entered, setEntered] = useState(false);
  const [opensAbove, setOpensAbove] = useState(false);
  const typeahead = useRef<{ buffer: string; expiresAt: number }>({ buffer: "", expiresAt: 0 });

  const measure = useCallback(() => {
    const rect = menu.triggerRef.current?.getBoundingClientRect();
    const contentRect = menu.contentRef.current?.getBoundingClientRect();
    if (!rect || !contentRect) return;
    // clientWidth, not window.innerWidth: fixed-position `right` is measured
    // from the viewport edge inside the scrollbar, while innerWidth includes
    // the scrollbar — mixing them shifts the menu left by the scrollbar width.
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    const belowTop = rect.bottom + CONTENT_GAP_PX;
    const aboveTop = rect.top - CONTENT_GAP_PX - contentRect.height;
    const desiredTop = contentRect.height > viewportHeight - belowTop ? aboveTop : belowTop;
    const top = clampToViewport(desiredTop, contentRect.height, viewportHeight);
    setOpensAbove(desiredTop === aboveTop);

    if (align === "end") {
      const desiredRight = viewportWidth - rect.right;
      setPosition({
        top,
        right: clampToViewport(desiredRight, contentRect.width, viewportWidth),
      });
      return;
    }

    setPosition({
      top,
      left: clampToViewport(rect.left, contentRect.width, viewportWidth),
    });
  }, [align, menu.contentRef, menu.triggerRef]);

  useLayoutEffect(() => {
    if (!menu.open) {
      setEntered(false);
      return;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [menu.open]);

  // Layout effect so the first paint already has coordinates (no flash at
  // 0,0), then track scroll/resize while open — fixed positioning doesn't
  // follow the trigger on its own.
  useLayoutEffect(() => {
    if (!menu.open) return;
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [menu.open, measure]);

  if (!menu.open) return null;

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    handleMenuKeyDown(event, menu);
    if (event.defaultPrevented) return;
    // Menu typeahead: printable characters accumulate into a prefix that
    // moves focus to the first matching item, per the ARIA menu pattern.
    if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
      const now = Date.now();
      const state = typeahead.current;
      const buffer = (now > state.expiresAt ? "" : state.buffer) + event.key.toLowerCase();
      typeahead.current = { buffer, expiresAt: now + TYPEAHEAD_RESET_MS };
      const match = dropdownItems(menu.contentRef.current).find((item) =>
        (item.textContent ?? "").trim().toLowerCase().startsWith(buffer),
      );
      if (match) {
        event.preventDefault();
        match.focus({ preventScroll: true });
      }
    }
  };

  return (
    <OverlayPortal>
      <div
        className={cn(
          "fixed z-100 min-w-56 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-1 motion-reduce:scale-100 [transition:opacity_var(--motion-base),transform_var(--motion-base)]",
          opensAbove
            ? align === "end"
              ? "origin-bottom-right"
              : "origin-bottom-left"
            : align === "end"
              ? "origin-top-right"
              : "origin-top-left",
          entered ? "scale-100 opacity-100" : "scale-[0.96] opacity-0",
          className,
        )}
        onKeyDown={handleKeyDown}
        ref={menu.contentRef}
        role="menu"
        style={position ?? { visibility: "hidden" }}
      >
        {children}
      </div>
    </OverlayPortal>
  );
}

function clampToViewport(coordinate: number, contentSize: number, viewportSize: number): number {
  return Math.min(Math.max(0, coordinate), Math.max(0, viewportSize - contentSize));
}

export function DropdownMenuItem({
  children,
  className,
  disabled = false,
  href,
  onSelect,
  tone = "default",
}: DropdownMenuItemProps) {
  const menu = useDropdownMenu();
  const itemClass = cn(
    "flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-left text-sm transition-colors duration-100 focus-visible:outline-none",
    tone === "danger"
      ? "text-[var(--color-danger)] hover:bg-[var(--color-danger-soft)] focus-visible:bg-[var(--color-danger-soft)]"
      : "text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)] focus-visible:bg-[var(--color-surface-2)] focus-visible:text-[var(--color-text)]",
    disabled && "cursor-not-allowed opacity-60 hover:bg-transparent hover:text-inherit",
    className,
  );

  if (href) {
    return (
      <RouterLink
        className={itemClass}
        href={href}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          // Closes without restoring focus, unlike the action path: following a
          // link hands focus to the destination, and the shell's route focus
          // reset moves it to the new page's heading. Pulling it back to a
          // trigger on the page just left would defeat that.
          menu.setOpen(false);
        }}
        aria-disabled={disabled ? "true" : undefined}
        role="menuitem"
        tabIndex={-1}
      >
        {children}
      </RouterLink>
    );
  }

  return (
    <button
      aria-disabled={disabled ? "true" : undefined}
      className={itemClass}
      onClick={() => {
        if (disabled) return;
        // Restore before `onSelect`, not after: an action that opens a dialog
        // or focuses a field has to win, and it can only do so if the restore
        // has already happened. It also decides what the dialog it opens
        // records as the element to give focus back to on close.
        menu.closeToTrigger();
        onSelect?.();
      }}
      role="menuitem"
      tabIndex={-1}
      type="button"
    >
      {children}
    </button>
  );
}

export function DropdownMenuSeparator() {
  return <hr className="my-[var(--space-1)] border-0 border-t border-[var(--color-border)]" />;
}

/** The enclosing menu's open state, refs and focus moves. */
function useDropdownMenu(): DropdownContextValue {
  const value = useContext(DropdownContext);
  if (!value) {
    throw new Error("DropdownMenu components must be used inside DropdownMenu");
  }
  return value;
}

function handleMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>, menu: DropdownContextValue) {
  const items = dropdownItems(menu.contentRef.current);
  if (items.length === 0) return;

  const currentIndex = items.indexOf(document.activeElement as HTMLElement);
  if (event.key === "ArrowDown") {
    event.preventDefault();
    items[(currentIndex + 1) % items.length]?.focus({ preventScroll: true });
  }
  if (event.key === "ArrowUp") {
    event.preventDefault();
    items[(currentIndex <= 0 ? items.length : currentIndex) - 1]?.focus({ preventScroll: true });
  }
  if (event.key === "Home") {
    event.preventDefault();
    items[0]?.focus({ preventScroll: true });
  }
  if (event.key === "End") {
    event.preventDefault();
    items.at(-1)?.focus({ preventScroll: true });
  }
}

function dropdownItems(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>("[role='menuitem']")).filter(
    (item) => item.getClientRects().length > 0,
  );
}
